import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';

import { t } from '../../common/i18n';
import {
  Button,
  CheckboxField,
  DateField,
  FileField,
  FormMessage,
  PhoneField,
  SingleSelectField,
  TextField,
  TipMessage,
} from '../../common/components';
import Yup, { YupEmail, YupEmailNonRequired, YupPhone, YupPhoneNonRequired } from '../../common/utils/FormValidation';
import { formatDate } from '../../common/utils/Date/Date';
import { getProfileName } from '../../common/utils/Profile/profile';
import { TSFixMe } from '../../frontend-libs/evlapp-types';

import { RepresentativeEmailCheckStatus, checkRepresentativeEmail } from '../services/RepresentationService';
import { getUnits } from '../services/UnitService';

interface MemberOption {
  value: string;
  label: string;
}

interface MemberOptionGroup {
  label: string;
  options: MemberOption[];
}

interface RepresentativeFormProps {
  /** Odeslání formuláře — dostane hotová multipart data (representativeProfileId NEBO nová osoba + validTo + documents[]). */
  onSubmit: (values: FormData) => Promise<void>;
  submitLabel: string;
  /** Profil, který nemá být v nabídce členů (např. zastoupený u plné moci). */
  excludeProfileId?: string;
  /** Profily zvýrazněné jako první skupina selectu (typicky spoluvlastníci jednotky). */
  preferredProfileIds?: string[];
}

/**
 * Sdílený formulář určení zástupce (společný zástupce jednotky / plná moc):
 * výběr existujícího člena, nebo přizvání nové osoby, platnost do a přílohy (plné moci).
 */
export const RepresentativeForm: React.FC<RepresentativeFormProps> = ({
  onSubmit,
  submitLabel,
  excludeProfileId,
  preferredProfileIds,
}) => {
  const namespace = 'Units.RepresentativeForm';

  const [memberOptions, setMemberOptions] = useState<MemberOptionGroup[]>([]);
  const [submitError, setSubmitError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Předběžná kontrola e-mailu osoby mimo dům — po dopsání (debounce) nebo při opuštění pole
  const [emailCheck, setEmailCheck] = useState<{ email: string; status: RepresentativeEmailCheckStatus } | null>(null);
  const emailCheckTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    },
    []
  );

  const runEmailCheck = (email: string) => {
    const value = email.trim();
    // API voláme až pro smysluplně vypadající e-mail
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailCheck(null);
      return;
    }

    checkRepresentativeEmail(value)
      .then((res) => setEmailCheck({ email: value, status: res.status }))
      .catch(() => setEmailCheck(null));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setEmailCheck(null);
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    const value = e.target.value;
    emailCheckTimer.current = window.setTimeout(() => runEmailCheck(value), 600);
  };

  const handleEmailBlur = () => {
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    runEmailCheck(formik.values.email || '');
  };

  useEffect(() => {
    // Nabídka členů se skládá z vlastníků jednotek (unikátně); label nese jméno + čísla jednotek.
    // Spoluvlastníci jednotky (preferredProfileIds) tvoří první skupinu, zbytek domu druhou.
    getUnits()
      .then((units: TSFixMe) => {
        const byProfile = new Map<string, { name: string; units: string[] }>();
        (units ?? []).forEach((unit: TSFixMe) => {
          (unit.owners ?? []).forEach((owner: TSFixMe) => {
            const profile = owner.profile ?? owner;
            if (!profile?.id || profile.id === excludeProfileId) return;
            const entry = byProfile.get(profile.id) ?? { name: getProfileName(profile), units: [] };
            if (unit.label) entry.units.push(unit.label);
            byProfile.set(profile.id, entry);
          });
        });

        const options = Array.from(byProfile.entries())
          .map(([id, entry]) => ({
            value: id,
            label: entry.units.length > 0 ? `${entry.name} (č. ${entry.units.join(', ')})` : entry.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'cs'));

        const preferred = new Set(preferredProfileIds ?? []);
        if (preferred.size === 0) {
          setMemberOptions([{ label: t('Groups.members', namespace), options }]);
          return;
        }

        setMemberOptions([
          { label: t('Groups.unitOwners', namespace), options: options.filter((o) => preferred.has(o.value)) },
          { label: t('Groups.others', namespace), options: options.filter((o) => !preferred.has(o.value)) },
        ]);
      })
      .catch(() => setMemberOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeProfileId, preferredProfileIds?.join(',')]);

  const formik = useFormik<TSFixMe>({
    initialValues: {
      isNewPerson: false,
      representativeProfileId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      validTo: '',
      documents: null,
    },
    validate: () => setSubmitError(false),
    validationSchema: Yup.object({
      isNewPerson: Yup.boolean(),
      representativeProfileId: Yup.string().when('isNewPerson', {
        is: false,
        then: (schema) => schema.required(),
      }),
      firstName: Yup.string().when('isNewPerson', { is: true, then: (schema) => schema.required() }),
      lastName: Yup.string().when('isNewPerson', { is: true, then: (schema) => schema.required() }),
      // POZOR: YupEmail má required už v základu — přímé použití by tiše blokovalo submit
      // i při výběru existujícího člena (e-mail je pak prázdný a skrytý)
      email: YupEmailNonRequired.when('isNewPerson', { is: true, then: () => YupEmail }),
      // Telefon je u nové osoby povinný — bez něj by zástupce nemohl potvrdit hlas přes SMS
      phone: YupPhoneNonRequired.when('isNewPerson', { is: true, then: () => YupPhone }),
    }),
    onSubmit: async (values) => {
      // Vlastník z tohoto domu se vybírá v seznamu členů — ruční e-mail by navázal zastoupení
      // na jinak pojmenovaný profil (hláška s vysvětlením už je zobrazená, BE by vrátil 422)
      if (
        values.isNewPerson &&
        emailCheck &&
        emailCheck.email === (values.email || '').trim() &&
        emailCheck.status === 'owner'
      ) {
        formik.setSubmitting(false);
        return;
      }

      const formData = new FormData();
      if (values.isNewPerson) {
        formData.append('firstName', values.firstName);
        formData.append('lastName', values.lastName);
        formData.append('email', values.email);
        if (values.phone) formData.append('phone', values.phone);
      } else {
        formData.append('representativeProfileId', values.representativeProfileId);
      }
      if (values.validTo) formData.append('validTo', formatDate(values.validTo, true));
      if (values.documents) {
        for (let i = 0; i < values.documents.length; i++) {
          formData.append(`documents[${i}]`, values.documents[i]);
        }
      }

      try {
        await onSubmit(formData);
      } catch {
        setSubmitError(true);
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const selectedMember =
    memberOptions
      .flatMap((group) => group.options)
      .find((option) => option.value === formik.values.representativeProfileId) ?? null;

  return (
    <form onSubmit={formik.handleSubmit}>
      {submitError && <FormMessage message={t('error', namespace)} variant="dark" />}
      {/* SingleSelectField chybu nevykresluje — bez souhrnné hlášky by nevalidní submit vypadal, že „nic nedělá" */}
      {formik.submitCount > 0 && !formik.isValid && <FormMessage message={t('invalid', namespace)} variant="dark" />}

      {!formik.values.isNewPerson && (
        <SingleSelectField
          label={t('Fields.member.label', namespace)}
          placeholder={t('Fields.member.placeholder', namespace)}
          name="representativeProfileId"
          value={selectedMember}
          onChange={(option) =>
            formik.setFieldValue('representativeProfileId', (option as MemberOption | null)?.value ?? '')
          }
          options={memberOptions}
          className="mb-2"
          error={{
            message: formik.errors.representativeProfileId as string,
            touched: formik.touched.representativeProfileId as boolean,
          }}
        />
      )}

      <CheckboxField
        name="isNewPerson"
        label={t('Fields.isNewPerson.label', namespace)}
        checked={formik.values.isNewPerson}
        onChange={(e: TSFixMe) => formik.setFieldValue('isNewPerson', e.target.checked)}
        className="mb-2"
      />

      {formik.values.isNewPerson && (
        <>
          <TextField
            name="firstName"
            label={t('Fields.firstName.label', namespace)}
            value={formik.values.firstName}
            onChange={formik.handleChange}
            className="mb-2"
            error={{ message: formik.errors.firstName as string, touched: formik.touched.firstName as boolean }}
          />
          <TextField
            name="lastName"
            label={t('Fields.lastName.label', namespace)}
            value={formik.values.lastName}
            onChange={formik.handleChange}
            className="mb-2"
            error={{ message: formik.errors.lastName as string, touched: formik.touched.lastName as boolean }}
          />
          <TextField
            name="email"
            label={t('Fields.email.label', namespace)}
            value={formik.values.email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            className="mb-2"
            error={{ message: formik.errors.email as string, touched: formik.touched.email as boolean }}
          />
          {emailCheck &&
            emailCheck.email === (formik.values.email || '').trim() &&
            emailCheck.status !== 'available' && (
              <TipMessage className="mb-2">{t(`EmailCheck.${emailCheck.status}`, namespace)}</TipMessage>
            )}
          <PhoneField
            name="phone"
            label={t('Fields.phone.label', namespace)}
            value={formik.values.phone}
            // PhoneField volá onChange se STRINGEM (E164) nebo undefined při smazání — formik.handleChange
            // by na undefined spadl (crash celé stránky přes ErrorBoundary)
            onChange={(phone) => formik.setFieldValue('phone', phone ?? '')}
            className="mb-2"
            error={{ message: formik.errors.phone as string, touched: formik.touched.phone as boolean }}
          />
        </>
      )}

      <DateField
        label={t('Fields.validTo.label', namespace)}
        name="validTo"
        value={formik.values.validTo}
        onChange={(date) => formik.setFieldValue('validTo', date)}
        className="mb-2"
        error={{ message: formik.errors.validTo as string, touched: formik.touched.validTo as boolean }}
      />

      <FileField
        name="documents"
        label={t('Fields.documents.label', namespace)}
        filesLabel="Units.RepresentativeForm.Fields.documents.filesLabel"
        multiple={true}
        accept="application/pdf,image/*"
        reference={fileInputRef}
        formikValue={formik.values.documents}
        className="mt-2 mb-3"
        onChange={(event) => {
          if (event && event.currentTarget && event.currentTarget.files) {
            formik.setFieldValue('documents', event.currentTarget.files);
          }
        }}
      />

      <div className="Flex FlexAlign--center FlexJustify--center mb-1">
        <Button
          onClick={formik.handleSubmit}
          loading={formik.isSubmitting}
          disabledDeps={['demo', 'expired']}
          title={submitLabel}
          wide={true}
        />
      </div>
    </form>
  );
};
